import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Container,
  Stack,
  Title,
  Text,
  Grid,
  Card,
  Group,
  Badge,
  Box,
  LoadingOverlay,
  Flex,
  Button,
  Paper,
  Alert,
  Avatar,
  List,
  Radio,
  Divider,
  CheckIcon
} from '@mantine/core';
import {
  IconCalendar,
  IconClock,
  IconCheck,
  IconAlertCircle,
  IconUser,
  IconSend,
  IconDownload,
  IconFile,
  IconUsers
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { assessmentScheduleService } from 'services/lia/assessmentScheduleService';
import NotFoundPage from 'pages/NotFound';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import utc from 'dayjs/plugin/utc';
import { getAssessmentAvailabilityStatus } from 'utils/assessmentStatus';

dayjs.extend(utc);

interface Evidence {
  evidenceID: string;
  evidenceUrl: string;
  fileName: string;
  fileSize: string;
  uploadDate: string;
  status?: 'valid' | 'invalid' | null;
}

interface Module {
  moduleID: string;
  moduleName?: string;
  trAssessmentModuleID?: string;
  evidences?: Evidence[];
}

interface Participant {
  binusianId: string;
  name: string;
  email?: string;
  type?: string;
  division?: string;
}

const SMEAssessmentDetailPage: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const encryptedEmail = searchParams.get('e') || '';

  const [formError, setFormError] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [evidenceStatuses, setEvidenceStatuses] = useState<
    Record<string, 'valid' | 'invalid' | null>
  >({});

  // Fetch assessment data
  const {
    data: assessment,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['smeAssessmentDetail', encryptedEmail],
    queryFn: () => {
      try {
        return assessmentScheduleService.getSMEDetailAssessmentSchedule(encryptedEmail);
      } catch {
        return mockAssessmentData;
      }
    },
    enabled: !!encryptedEmail,
    refetchOnWindowFocus: false
  });

  // Initialize evidence statuses when data loads
  useEffect(() => {
    if (assessment?.modules) {
      const initialStatuses: Record<string, 'valid' | 'invalid' | null> = {};

      assessment.modules.forEach((module: Module) => {
        if (module.evidences) {
          module.evidences.forEach((evidence: Evidence) => {
            initialStatuses[evidence.evidenceID] = evidence.status || null;
          });
        }
      });

      setEvidenceStatuses(initialStatuses);
    }
  }, [assessment]);

  // Handle evidence status change
  const handleEvidenceStatusChange = (evidenceId: string, status: 'valid' | 'invalid') => {
    setEvidenceStatuses((prev) => ({
      ...prev,
      [evidenceId]: status
    }));
    setFormError('');
  };

  /* eslint-disable */
  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: (evidenceData: { evidences: { evidenceID: string; status: string }[] }) => {
      // When API is ready, uncomment this
      // return assessmentScheduleService.submitSMEEvidenceStatus(encryptedEmail, evidenceData);

      // For now, just mock a successful response
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, message: 'Evidence validation submitted successfully' });
        }, 1000);
      });
    },
    onSuccess: () => {
      setFormSubmitted(true);
      showSuccessNotification({
        message: 'Evidence validation submitted successfully'
      });
      refetch();
    },
    onError: (error: any) => {
      setFormError(error.message || 'Failed to submit evidence validation. Please try again.');
      showErrorNotification({
        message: 'Error submitting evidence validation'
      });
    }
  });
  /* eslint-enable */

  const handleSubmit = () => {
    if (!assessment || !assessment.modules) return;

    const evidencePayload = Object.entries(evidenceStatuses)
      .filter(([_, status]) => status !== null)
      .map(([evidenceID, status]) => ({
        evidenceID,
        status: status as string
      }));

    const totalEvidences = assessment.modules.reduce(
      (count: any, module: any) => count + (module.evidences?.length || 0),
      0
    );

    const reviewedEvidences = evidencePayload.length;

    if (reviewedEvidences < totalEvidences) {
      setFormError(
        `Please review all evidence items (${reviewedEvidences}/${totalEvidences} reviewed).`
      );
      return;
    }

    submitMutation.mutate({ evidences: evidencePayload });
  };

  const getFileExtension = (filename: string) => {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
  };

  // Helper function to get icon for file type
  const getFileIcon = (filename: string) => {
    const ext = getFileExtension(filename).toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
      return <IconFile size={24} color='#4299e1' />;
    } else if (['pdf'].includes(ext)) {
      return <IconFile size={24} color='#e53e3e' />;
    } else if (['doc', 'docx'].includes(ext)) {
      return <IconFile size={24} color='#3182ce' />;
    } else if (['ppt', 'pptx'].includes(ext)) {
      return <IconFile size={24} color='#dd6b20' />;
    } else if (['xls', 'xlsx'].includes(ext)) {
      return <IconFile size={24} color='#38a169' />;
    } else {
      return <IconFile size={24} />;
    }
  };

  const getReviewedCount = () => {
    if (!assessment || !assessment.modules) return { total: 0, reviewed: 0 };

    const total = assessment.modules.reduce(
      (count: any, module: any) => count + (module.evidences?.length || 0),
      0
    );

    const reviewed = Object.values(evidenceStatuses).filter((status) => status !== null).length;

    return { total, reviewed };
  };

  if (!encryptedEmail) {
    return (
      <Container size='lg' py='xl'>
        <Title order={2} mb='md'>
          SME Assessment Review
        </Title>
        <Paper p='md' withBorder>
          <Text color='red'>
            Missing required parameters. Please use the link provided in your email.
          </Text>
        </Paper>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container fluid>
        <LoadingOverlay visible={true} />
      </Container>
    );
  }

  if (error || !assessment) {
    return <NotFoundPage />;
  }

  // Calculate status
  const status = getAssessmentAvailabilityStatus(
    assessment.startTime,
    assessment.endTime,
    assessment.isDeleted
  );

  const sme = assessment.sme || {};
  const { reviewed, total } = getReviewedCount();

  return (
    <Container size='lg' py={{ base: 'md', sm: 'xl' }}>
      <Grid gutter='lg'>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap='md'>
            <Card
              withBorder
              radius='lg'
              padding='lg'
              style={{ backgroundColor: '#0F172A', color: '#fff' }}
            >
              <Group justify='space-between' align='flex-start'>
                <Stack gap={14}>
                  <Group gap='xs'>
                    {status === 'completed' ? (
                      <Badge color='teal' variant='light' size='lg' radius='sm'>
                        Completed on {dayjs.utc(assessment.endTime).local().format('DD/MM/YYYY')}
                      </Badge>
                    ) : status === 'in progress' ? (
                      <Badge color='yellow' variant='light' size='lg' radius='sm'>
                        In Progress
                      </Badge>
                    ) : status === 'cancelled' ? (
                      <Badge color='red' variant='light' size='lg' radius='sm'>
                        Cancelled
                      </Badge>
                    ) : (
                      <Badge color='blue' variant='light' size='lg' radius='sm'>
                        Pending
                      </Badge>
                    )}
                  </Group>

                  <Title order={2} c='#fff'>
                    {assessment.name}
                  </Title>

                  <Group gap='xs'>
                    <Badge color='gray' bg='gray' variant='solid' size='sm'>
                      {assessment.type}
                    </Badge>
                    <Badge color='gray' bg='gray' variant='solid' size='sm'>
                      {assessment.typeAssessment || 'Team'}
                    </Badge>
                    {assessment.source && (
                      <Badge color='gray' bg='gray' variant='solid' size='sm'>
                        Source: {assessment.source}
                      </Badge>
                    )}
                  </Group>
                </Stack>
              </Group>
            </Card>

            <Card withBorder radius='lg' padding='lg'>
              <Title order={4} mb='md'>
                About this assessment
              </Title>
              <Text>{assessment.description}</Text>

              <Title order={4} mt='xl' mb='md'>
                Learning Objective
              </Title>
              <Text>{assessment.learningObjectives}</Text>
            </Card>

            <Card withBorder radius='lg' padding='lg'>
              <Title order={4} mb='md'>
                Assessment Evidence Validation
              </Title>

              {status !== 'in progress' ? (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title='Assessment Not Active'
                  color='blue'
                >
                  {status === 'pending'
                    ? 'This assessment has not started yet.'
                    : 'This assessment has already ended.'}
                </Alert>
              ) : (
                <>
                  {sme && sme.name && (
                    <Paper p='md' withBorder mb='xl'>
                      <Group gap='md'>
                        <Avatar color='#7B2183' radius='xl' size='md'>
                          {sme.name?.charAt(0)}
                        </Avatar>
                        <div>
                          <Text size='sm' fw={500}>
                            You are reviewing as:
                          </Text>
                          <Text fw={700}>{sme.name}</Text>
                          <Text size='xs' color='dimmed'>
                            Subject Matter Expert
                          </Text>
                        </div>
                      </Group>
                    </Paper>
                  )}

                  {/* Progress indicator */}
                  <Alert color={reviewed === total ? 'green' : 'blue'} mb='xl'>
                    <Group justify='space-between'>
                      <Text>Evidence Review Progress</Text>
                      <Badge color={reviewed === total ? 'green' : 'blue'} size='lg'>
                        {reviewed}/{total} Reviewed
                      </Badge>
                    </Group>
                  </Alert>

                  {/* Form error message */}
                  {formError && (
                    <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red' mb='md'>
                      {formError}
                    </Alert>
                  )}

                  {/* Success message */}
                  {formSubmitted && (
                    <Alert icon={<IconCheck size={16} />} title='Success' color='green' mb='md'>
                      Your evidence validation has been submitted successfully.
                    </Alert>
                  )}

                  {/* Evidence items by module */}
                  {assessment.modules && assessment.modules.length > 0 ? (
                    assessment.modules.map((module: Module, moduleIndex: number) => (
                      <Box key={module.moduleID || `module-${moduleIndex}`} mb='xl'>
                        <Title order={5} mb='md'>
                          {module.moduleName || `Module ${moduleIndex + 1}`}
                        </Title>

                        {module.evidences && module.evidences.length > 0 ? (
                          <Stack gap='md'>
                            {module.evidences.map((evidence: Evidence) => (
                              <Paper
                                key={evidence.evidenceID}
                                p='md'
                                withBorder
                                style={{
                                  borderColor:
                                    evidenceStatuses[evidence.evidenceID] === 'valid'
                                      ? '#38a169'
                                      : evidenceStatuses[evidence.evidenceID] === 'invalid'
                                        ? '#e53e3e'
                                        : undefined,
                                  borderWidth: evidenceStatuses[evidence.evidenceID] ? '2px' : '1px'
                                }}
                              >
                                <Stack gap='md'>
                                  <Group justify='space-between'>
                                    <Group gap='md'>
                                      {getFileIcon(evidence.fileName)}
                                      <div>
                                        <Text fw={500}>{evidence.fileName}</Text>
                                        <Text size='xs' color='dimmed'>
                                          {evidence.fileSize} • Uploaded{' '}
                                          {dayjs(evidence.uploadDate).format('DD/MM/YYYY HH:mm')}
                                        </Text>
                                      </div>
                                    </Group>
                                    <Button
                                      variant='light'
                                      color='blue'
                                      size='xs'
                                      leftSection={<IconDownload size={16} />}
                                      component='a'
                                      href={evidence.evidenceUrl}
                                      target='_blank'
                                      download
                                    >
                                      Download
                                    </Button>
                                  </Group>

                                  <Divider />

                                  <Stack gap='sm'>
                                    <Text fw={500}>Validation Status:</Text>
                                    <Radio.Group
                                      value={evidenceStatuses[evidence.evidenceID] || ''}
                                      onChange={(value) =>
                                        handleEvidenceStatusChange(
                                          evidence.evidenceID,
                                          value as 'valid' | 'invalid'
                                        )
                                      }
                                      name={`evidence-status-${evidence.evidenceID}`}
                                    >
                                      <Group>
                                        <Radio
                                          value='valid'
                                          label={
                                            <Text fw={500} c='green'>
                                              Valid
                                            </Text>
                                          }
                                          icon={CheckIcon}
                                          color='green'
                                        />
                                        <Radio
                                          value='invalid'
                                          label={
                                            <Text fw={500} c='red'>
                                              Invalid
                                            </Text>
                                          }
                                          icon={CheckIcon}
                                          color='red'
                                        />
                                      </Group>
                                    </Radio.Group>
                                  </Stack>
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        ) : (
                          <Alert color='blue' icon={<IconAlertCircle size={16} />}>
                            No evidence has been uploaded for this module.
                          </Alert>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Alert color='blue' icon={<IconAlertCircle size={16} />}>
                      No modules are available for this assessment.
                    </Alert>
                  )}

                  {/* Action button */}
                  <Group justify='flex-end' mt='xl'>
                    <Button
                      color='#7B2183'
                      leftSection={<IconSend size={16} />}
                      onClick={handleSubmit}
                      loading={submitMutation.isPending}
                      disabled={formSubmitted || reviewed < total}
                      style={{ opacity: reviewed < total ? 0.7 : 1 }}
                    >
                      Submit Validation
                    </Button>
                  </Group>
                </>
              )}
            </Card>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card withBorder radius='lg' padding='lg'>
            <Stack gap='md'>
              <Flex gap='xs' align='center'>
                <IconClock size={20} />
                <Title order={4}>Timeline</Title>
              </Flex>

              <Group gap='xs' wrap='nowrap'>
                <IconCalendar size={20} />
                <Stack gap={0}>
                  <Text size='sm' fw={500}>
                    Start Date
                  </Text>
                  <Text size='sm'>
                    {dayjs.utc(assessment.startTime).local().format('DD/MM/YYYY HH:mm')}
                  </Text>
                </Stack>
              </Group>

              <Group gap='xs' wrap='nowrap'>
                <IconCalendar size={20} />
                <Stack gap={0}>
                  <Text size='sm' fw={500}>
                    End Date
                  </Text>
                  <Text size='sm'>
                    {dayjs.utc(assessment.endTime).local().format('DD/MM/YYYY HH:mm')}
                  </Text>
                </Stack>
              </Group>

              <div style={{ margin: '12px 0', height: '1px', backgroundColor: '#e0e0e0' }} />

              {/* Assessment Type */}
              <Flex gap='xs' align='center'>
                {assessment.typeAssessment === 'Individu' ? (
                  <IconUser size={20} />
                ) : (
                  <IconUsers size={20} />
                )}
                <Title order={4}>Assessment Type</Title>
              </Flex>

              <Paper p='sm' withBorder>
                <Group>
                  <Badge
                    color={assessment.typeAssessment === 'Individu' ? 'blue' : 'violet'}
                    size='lg'
                  >
                    {assessment.typeAssessment || 'Team'}
                  </Badge>
                  <Text size='sm'>
                    {assessment.typeAssessment === 'Individu'
                      ? 'Individual assessment'
                      : 'Team assessment'}
                  </Text>
                </Group>
              </Paper>

              <div style={{ margin: '12px 0', height: '1px', backgroundColor: '#e0e0e0' }} />

              {/* Assessment To Section */}
              {assessment.participants && assessment.participants.length > 0 && (
                <>
                  <Flex gap='xs' align='center'>
                    <IconUser size={20} />
                    <Title order={4}>Assessment Participants</Title>
                  </Flex>

                  {assessment.participants.map((participant: Participant, index: number) => (
                    <Paper key={index} p='sm' withBorder>
                      <Group>
                        <Avatar color='#7B2183' radius='xl'>
                          {participant.name ? participant.name.charAt(0) : 'P'}
                        </Avatar>
                        <Stack gap={0}>
                          <Text fw={600}>{participant.name}</Text>
                          <Text size='xs' color='dimmed'>
                            {participant.email || participant.binusianId}
                          </Text>
                        </Stack>
                      </Group>
                    </Paper>
                  ))}
                </>
              )}

              <div style={{ margin: '12px 0', height: '1px', backgroundColor: '#e0e0e0' }} />

              <Flex gap='xs' align='center'>
                <IconFile size={20} />
                <Title order={4}>Evidence Summary</Title>
              </Flex>

              <List spacing='xs'>
                {assessment.modules &&
                  assessment.modules.map((module: Module) => {
                    const evidenceCount = module.evidences?.length || 0;
                    const validCount =
                      module.evidences?.filter((e) => evidenceStatuses[e.evidenceID] === 'valid')
                        .length || 0;
                    const invalidCount =
                      module.evidences?.filter((e) => evidenceStatuses[e.evidenceID] === 'invalid')
                        .length || 0;

                    return (
                      <List.Item key={module.moduleID || `module-summary-${module.moduleName}`}>
                        <Paper p='xs' withBorder>
                          <Text fw={500} size='sm' mb='xs'>
                            {module.moduleName || `Module ${module.moduleID}`}
                          </Text>
                          <Group>
                            <Badge color='blue'>{evidenceCount} Total</Badge>
                            <Badge color='green'>{validCount} Valid</Badge>
                            <Badge color='red'>{invalidCount} Invalid</Badge>
                          </Group>
                        </Paper>
                      </List.Item>
                    );
                  })}
              </List>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>
    </Container>
  );
};

const mockAssessmentData = {
  msAssessmentScheduleID: 'mock-id',
  name: 'UI/UX Product Assessment',
  type: 'product',
  programId: 'prog123',
  programName: 'Design Thinking Program',
  typeAssessment: 'Team',
  learningObjectives: 'Evaluate product design principles and implementation',
  description:
    'This assessment evaluates how well participants apply design thinking principles to create user-centered products.',
  isWithQuestion: 'N',
  startTime: '2025-04-01T16:00:00',
  endTime: '2025-04-30T16:00:00',
  notificationStatus: 'sent',
  statusKurikulum: true,
  isBeforeSchedule: false,
  notificationSchedule: new Date().toISOString(),
  isDeleted: false,
  dateIn: new Date().toISOString(),
  dateUp: new Date().toISOString(),
  userIn: 'system',
  userUp: 'system',
  sme: {
    name: 'John Doe',
    email: 'john.doe@example.com',
    role: 'Subject Matter Expert'
  },
  participants: [
    {
      binusianId: 'B12345',
      name: 'Team Alpha',
      email: 'team.alpha@example.com',
      type: 'Team'
    }
  ],
  modules: [
    {
      moduleID: 'mod1',
      moduleName: 'User Research',
      evidences: [
        {
          evidenceID: 'ev1',
          evidenceUrl: 'https://example.com/evidence/file1.pdf',
          fileName: 'user_research_report.pdf',
          fileSize: '1.5 MB',
          uploadDate: new Date().toISOString()
        },
        {
          evidenceID: 'ev2',
          evidenceUrl: 'https://example.com/evidence/file2.jpg',
          fileName: 'user_personas.jpg',
          fileSize: '0.8 MB',
          uploadDate: new Date().toISOString()
        }
      ]
    },
    {
      moduleID: 'mod2',
      moduleName: 'Wireframing & Prototyping',
      evidences: [
        {
          evidenceID: 'ev3',
          evidenceUrl: 'https://example.com/evidence/file3.pdf',
          fileName: 'wireframes.pdf',
          fileSize: '2.2 MB',
          uploadDate: new Date().toISOString()
        },
        {
          evidenceID: 'ev4',
          evidenceUrl: 'https://example.com/evidence/file4.pptx',
          fileName: 'prototype_presentation.pptx',
          fileSize: '3.5 MB',
          uploadDate: new Date().toISOString()
        }
      ]
    }
  ]
};

export default SMEAssessmentDetailPage;
